import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  BellOff,
  FlaskConical,
  Laptop,
  Pill,
  ScanHeart,
  Trash2,
} from "lucide-react";
import { api, ApiError, okSchema } from "@/lib/api";
import { normalizeUrgency, URGENCY_META } from "@/lib/urgency";
import { PageHeader } from "@/components/layout/AppShell";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge, UrgencyBadge } from "@/components/ui/Badge";
import { Input } from "@/components/ui/Field";
import { Alert, EmptyState, LoadingPanel } from "@/components/ui/Feedback";
import { formatDate } from "@/components/ui/DataTable";
import { useToast } from "@/components/ui/toast";
import { sessionQueryKey } from "@/lib/session";
import { PasskeyManager } from "@/components/PasskeyManager";
import { cn } from "@/lib/cn";

function ScreenError({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <Alert tone="danger" title="Gagal memuat data" className="mb-5">
      {error instanceof ApiError ? error.message : "Terjadi kesalahan tak terduga."}
    </Alert>
  );
}

// --------------------------------------------------------------------------
// Notifications
// --------------------------------------------------------------------------

const notificationSchema = z.object({
  items: z
    .array(
      z.object({
        id: z.number(),
        title: z.string(),
        message: z.string(),
        type: z.string(),
        link: z.string().nullable().default(null),
        is_read: z.boolean(),
        created_at: z.string().nullable().default(null),
      }),
    )
    .default([]),
});

export function PatientNotifications() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { data, isPending, error } = useQuery({
    queryKey: ["patient", "notifications"],
    queryFn: ({ signal }) =>
      api.get("/api/patient/notifications", notificationSchema, signal),
  });

  const markAll = useMutation({
    mutationFn: () => api.post("/api/patient/notifications/read-all", okSchema),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["patient", "notifications"] });
      // The sidebar badge reads its count from the session payload.
      void queryClient.invalidateQueries({ queryKey: sessionQueryKey });
      toast.success({ title: "Semua notifikasi ditandai terbaca" });
    },
  });

  const unread = (data?.items ?? []).filter((item) => !item.is_read).length;

  return (
    <>
      <PageHeader
        eyebrow="Pemberitahuan"
        title="Notifikasi"
        description="Kabar dari tim medis dan hasil pemeriksaan Anda."
        actions={
          unread > 0 && (
            <Button loading={markAll.isPending} onClick={() => markAll.mutate()}>
              Tandai semua terbaca
            </Button>
          )
        }
      />
      <ScreenError error={error} />
      <Card>
        {isPending ? (
          <LoadingPanel />
        ) : data && data.items.length > 0 ? (
          <ul className="divide-hairline divide-y">
            {data.items.map((item) => (
              <li
                key={item.id}
                className={cn("flex gap-3 px-5 py-4", !item.is_read && "bg-accent/[0.04]")}
              >
                <span
                  aria-hidden
                  className={cn(
                    "mt-1.5 size-2 shrink-0 rounded-full",
                    item.is_read ? "bg-hairline-strong" : "bg-accent",
                  )}
                />
                <div className="min-w-0 flex-1">
                  <p className="font-semibold">{item.title}</p>
                  <p className="text-content-muted mt-0.5 text-sm">{item.message}</p>
                  <p className="text-content-subtle mt-1 text-xs">
                    {formatDate(item.created_at, true)}
                  </p>
                </div>
                {item.link && (
                  <Link
                    to={item.link}
                    className="text-accent shrink-0 self-center text-xs font-bold hover:underline"
                  >
                    Buka
                  </Link>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState
            icon={<BellOff className="size-5" />}
            title="Belum ada notifikasi"
            description="Pemberitahuan dari RS Mediora akan muncul di sini."
          />
        )}
      </Card>
    </>
  );
}

// --------------------------------------------------------------------------
// Health hub: history + labs
// --------------------------------------------------------------------------

const historySchema = z.object({
  items: z
    .array(
      z.object({
        session_uuid: z.string(),
        status: z.string(),
        check_type: z.string(),
        urgency: z.string(),
        summary: z.string().nullable().default(null),
        started_at: z.string().nullable().default(null),
        completed_at: z.string().nullable().default(null),
      }),
    )
    .default([]),
});

const labSchema = z.object({
  items: z
    .array(
      z.object({
        id: z.number(),
        test_name: z.string(),
        result_value: z.string().nullable().default(null),
        unit: z.string().nullable().default(null),
        reference_range: z.string().nullable().default(null),
        status: z.string().nullable().default(null),
        result_date: z.string().nullable().default(null),
        ai_explanation: z.string().nullable().default(null),
      }),
    )
    .default([]),
});

export function PatientHealthHub() {
  const history = useQuery({
    queryKey: ["patient", "health-history"],
    queryFn: ({ signal }) => api.get("/api/patient/health/history", historySchema, signal),
  });
  const labs = useQuery({
    queryKey: ["patient", "labs"],
    queryFn: ({ signal }) => api.get("/api/patient/labs", labSchema, signal),
  });

  return (
    <>
      <PageHeader
        eyebrow="Rekam kesehatan"
        title="Health Hub"
        description="Riwayat pemeriksaan mandiri dan hasil laboratorium Anda."
      />
      <ScreenError error={history.error ?? labs.error} />

      <div className="grid gap-4 lg:grid-cols-2 lg:items-start">
        <Card>
          <CardHeader eyebrow="Pemeriksaan mandiri" title="Riwayat cek kesehatan" />
          {history.isPending ? (
            <LoadingPanel />
          ) : history.data && history.data.items.length > 0 ? (
            <ul className="divide-hairline divide-y">
              {history.data.items.map((item) => {
                const level = normalizeUrgency(item.urgency);
                return (
                  <li key={item.session_uuid} className="flex items-start gap-3 px-5 py-3.5">
                    <span
                      aria-hidden
                      className={cn("mt-1.5 size-2 shrink-0 rounded-full", URGENCY_META[level].dot)}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <UrgencyBadge level={level} />
                        <span className="text-content-subtle text-xs">
                          {formatDate(item.completed_at ?? item.started_at)}
                        </span>
                      </div>
                      {item.summary && (
                        <p className="text-content-muted mt-1 line-clamp-2 text-sm">
                          {item.summary}
                        </p>
                      )}
                    </div>
                    <Link
                      to={`/patient/health-check/result/${item.session_uuid}`}
                      className="text-accent shrink-0 self-center text-xs font-bold hover:underline"
                    >
                      Detail
                    </Link>
                  </li>
                );
              })}
            </ul>
          ) : (
            <EmptyState
              icon={<ScanHeart className="size-5" />}
              title="Belum ada pemeriksaan"
              description="Mulai cek kesehatan untuk membangun riwayat Anda."
            />
          )}
        </Card>

        <Card>
          <CardHeader eyebrow="Laboratorium" title="Hasil lab" />
          {labs.isPending ? (
            <LoadingPanel />
          ) : labs.data && labs.data.items.length > 0 ? (
            <ul className="divide-hairline divide-y">
              {labs.data.items.map((item) => (
                <li key={item.id} className="px-5 py-3.5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-semibold">{item.test_name}</p>
                      <p className="text-content-muted tabular text-sm">
                        {item.result_value} {item.unit}
                        {item.reference_range && (
                          <span className="text-content-subtle">
                            {" "}
                            (acuan {item.reference_range})
                          </span>
                        )}
                      </p>
                      <p className="text-content-subtle mt-0.5 text-xs">
                        {formatDate(item.result_date)}
                      </p>
                    </div>
                    <Badge tone={item.status === "Normal" ? "routine" : "monitor"}>
                      {item.status ?? "—"}
                    </Badge>
                  </div>
                  {item.ai_explanation && (
                    <p className="bg-surface-sunken text-content-muted mt-2 rounded-lg px-3 py-2 text-xs">
                      {item.ai_explanation}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState
              icon={<FlaskConical className="size-5" />}
              title="Belum ada hasil lab"
              description="Hasil yang diunggah tim medis akan tampil di sini."
            />
          )}
        </Card>
      </div>
    </>
  );
}

// --------------------------------------------------------------------------
// Security: active sessions
// --------------------------------------------------------------------------

const sessionsSchema = z.object({
  items: z
    .array(
      z.object({
        id: z.number(),
        device: z.string(),
        ip_address: z.string().nullable().default(null),
        last_seen_at: z.string().nullable().default(null),
        created_at: z.string().nullable().default(null),
        revoked: z.boolean(),
      }),
    )
    .default([]),
});

export function PatientSecurity() {
  const { data, isPending, error } = useQuery({
    queryKey: ["patient", "sessions"],
    queryFn: ({ signal }) => api.get("/api/patient/sessions", sessionsSchema, signal),
  });

  const active = (data?.items ?? []).filter((item) => !item.revoked);

  return (
    <>
      <PageHeader
        eyebrow="Akun"
        title="Keamanan"
        description="Perangkat yang pernah masuk ke akun Portal Pasien Anda."
      />
      <ScreenError error={error} />

      <div className="mb-4">
        <PasskeyManager />
      </div>

      <Card>
        <CardHeader
          eyebrow="Sesi aktif"
          title={`${active.length} perangkat`}
          description="Jika ada perangkat yang tidak Anda kenali, segera ubah password Anda."
        />
        {isPending ? (
          <LoadingPanel />
        ) : active.length > 0 ? (
          <ul className="divide-hairline divide-y">
            {active.map((item) => (
              <li key={item.id} className="flex items-center gap-3 px-5 py-3.5">
                <Laptop aria-hidden className="text-content-subtle size-4 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{item.device}</p>
                  <p className="text-content-subtle truncate text-xs">
                    {item.ip_address ?? "IP tidak tercatat"} · terakhir{" "}
                    {formatDate(item.last_seen_at, true)}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState icon={<Laptop className="size-5" />} title="Tidak ada sesi aktif" />
        )}
      </Card>
    </>
  );
}

// --------------------------------------------------------------------------
// Medications
// --------------------------------------------------------------------------

const medicationSchema = z.object({
  items: z
    .array(
      z.object({
        id: z.number(),
        name: z.string(),
        dose: z.string().nullable().default(null),
        schedule_time: z.string().nullable().default(null),
        instructions: z.string().nullable().default(null),
        active: z.boolean(),
        taken_today: z.boolean(),
      }),
    )
    .default([]),
});

export function PatientMedications() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState({ name: "", dose: "", schedule_time: "" });

  const { data, isPending, error } = useQuery({
    queryKey: ["patient", "medications"],
    queryFn: ({ signal }) => api.get("/api/patient/medications", medicationSchema, signal),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["patient", "medications"] });
    void queryClient.invalidateQueries({ queryKey: ["patient", "dashboard"] });
  };

  const create = useMutation({
    mutationFn: () => api.post("/api/patient/medications", okSchema, form),
    onSuccess: () => {
      invalidate();
      setForm({ name: "", dose: "", schedule_time: "" });
      toast.success({ title: "Pengingat ditambahkan" });
    },
    onError: (cause) =>
      toast.danger({
        title: "Gagal menambahkan",
        description: cause instanceof ApiError ? cause.message : undefined,
      }),
  });

  const markTaken = useMutation({
    mutationFn: (id: number) => api.post(`/api/patient/medications/${id}/taken`, okSchema),
    onSuccess: () => {
      invalidate();
      toast.success({ title: "Ditandai sudah diminum" });
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.post(`/api/patient/medications/${id}/delete`, okSchema),
    onSuccess: () => {
      invalidate();
      toast.success({ title: "Pengingat dihapus" });
    },
  });

  return (
    <>
      <PageHeader
        eyebrow="Pengingat"
        title="Obat"
        description="Jadwal minum obat rutin Anda."
      />
      <ScreenError error={error} />

      <Card className="mb-4">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            create.mutate();
          }}
          className="grid gap-3 p-4 sm:grid-cols-[2fr_1fr_1fr_auto] sm:items-end"
        >
          <Input
            label="Nama obat"
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
            required
          />
          <Input
            label="Dosis"
            placeholder="500mg"
            value={form.dose}
            onChange={(event) => setForm({ ...form, dose: event.target.value })}
          />
          <Input
            label="Jam"
            type="time"
            value={form.schedule_time}
            onChange={(event) => setForm({ ...form, schedule_time: event.target.value })}
          />
          <Button type="submit" variant="primary" loading={create.isPending}>
            Tambah
          </Button>
        </form>
      </Card>

      <Card>
        {isPending ? (
          <LoadingPanel />
        ) : data && data.items.length > 0 ? (
          <ul className="divide-hairline divide-y">
            {data.items.map((item) => (
              <li key={item.id} className="flex items-center gap-3 px-5 py-3.5">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold">{item.name}</p>
                  <p className="text-content-muted truncate text-xs">
                    {[item.dose, item.schedule_time].filter(Boolean).join(" · ") || "—"}
                  </p>
                </div>
                {item.taken_today ? (
                  <Badge tone="routine" dot>
                    Sudah diminum
                  </Badge>
                ) : (
                  <Button
                    size="sm"
                    loading={markTaken.isPending && markTaken.variables === item.id}
                    onClick={() => markTaken.mutate(item.id)}
                  >
                    Tandai
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={`Hapus pengingat ${item.name}`}
                  loading={remove.isPending && remove.variables === item.id}
                  onClick={() => remove.mutate(item.id)}
                  icon={<Trash2 className="size-3.5" />}
                />
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState
            icon={<Pill className="size-5" />}
            title="Belum ada pengingat obat"
            description="Tambahkan obat rutin agar tidak terlewat."
          />
        )}
      </Card>
    </>
  );
}

// --------------------------------------------------------------------------
// Profile
// --------------------------------------------------------------------------

const profileSchema = z.object({
  rm_number: z.string(),
  name: z.string(),
  email: z.string().nullable().default(null),
  phone: z.string().nullable().default(null),
  dob: z.string().nullable().default(null),
  gender: z.string().nullable().default(null),
  address: z.string().nullable().default(null),
  no_bpjs: z.string().nullable().default(null),
  blood_type: z.string().nullable().default(null),
  allergies: z.string().nullable().default(null),
  chronic_conditions: z.string().nullable().default(null),
  routine_medications: z.string().nullable().default(null),
  emergency_name: z.string().nullable().default(null),
  emergency_phone: z.string().nullable().default(null),
});

type MedicalFields = Pick<
  z.infer<typeof profileSchema>,
  | "blood_type"
  | "allergies"
  | "chronic_conditions"
  | "routine_medications"
  | "emergency_name"
  | "emergency_phone"
>;

export function PatientProfile() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [draft, setDraft] = useState<Partial<MedicalFields>>({});

  const { data, isPending, error } = useQuery({
    queryKey: ["patient", "profile"],
    queryFn: ({ signal }) => api.get("/api/patient/profile", profileSchema, signal),
  });

  const save = useMutation({
    mutationFn: () => api.post("/api/patient/profile", okSchema, draft),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["patient", "profile"] });
      setDraft({});
      toast.success({ title: "Profil medis tersimpan" });
    },
    onError: (cause) =>
      toast.danger({
        title: "Gagal menyimpan",
        description: cause instanceof ApiError ? cause.message : undefined,
      }),
  });

  if (isPending) {
    return (
      <>
        <PageHeader title="Profil medis" />
        <Card>
          <LoadingPanel />
        </Card>
      </>
    );
  }

  if (error || !data) return <ScreenError error={error ?? new Error("kosong")} />;

  const field = (key: keyof MedicalFields) => draft[key] ?? data[key] ?? "";

  return (
    <>
      <PageHeader
        eyebrow={`Rekam medis ${data.rm_number}`}
        title="Profil medis"
        description="Informasi ini membantu tim medis saat keadaan darurat."
      />

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardHeader eyebrow="Identitas" title="Data dasar" />
          <CardBody className="space-y-2.5 text-sm">
            {[
              ["Nama", data.name],
              ["Email", data.email],
              ["Telepon", data.phone],
              ["Tanggal lahir", data.dob],
              ["Gender", data.gender],
              ["No. BPJS", data.no_bpjs],
            ].map(([label, value]) => (
              <div key={label} className="flex justify-between gap-3">
                <span className="text-content-subtle shrink-0">{label}</span>
                <span className="min-w-0 truncate text-right font-medium">{value || "—"}</span>
              </div>
            ))}
            <p className="text-content-subtle border-hairline mt-2 border-t pt-2.5 text-xs">
              Perubahan data identitas dilakukan melalui loket pendaftaran.
            </p>
          </CardBody>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader eyebrow="Riwayat" title="Informasi medis" />
          <CardBody>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                save.mutate();
              }}
              className="grid gap-4 sm:grid-cols-2"
            >
              <Input
                label="Golongan darah"
                placeholder="O+"
                value={field("blood_type")}
                onChange={(event) => setDraft({ ...draft, blood_type: event.target.value })}
              />
              <Input
                label="Alergi"
                placeholder="Penisilin, seafood"
                value={field("allergies")}
                onChange={(event) => setDraft({ ...draft, allergies: event.target.value })}
              />
              <Input
                label="Penyakit kronis"
                value={field("chronic_conditions")}
                onChange={(event) =>
                  setDraft({ ...draft, chronic_conditions: event.target.value })
                }
              />
              <Input
                label="Obat rutin"
                value={field("routine_medications")}
                onChange={(event) =>
                  setDraft({ ...draft, routine_medications: event.target.value })
                }
              />
              <Input
                label="Kontak darurat"
                value={field("emergency_name")}
                onChange={(event) => setDraft({ ...draft, emergency_name: event.target.value })}
              />
              <Input
                label="Telepon darurat"
                type="tel"
                value={field("emergency_phone")}
                onChange={(event) =>
                  setDraft({ ...draft, emergency_phone: event.target.value })
                }
              />
              <div className="sm:col-span-2">
                <Button
                  type="submit"
                  variant="primary"
                  loading={save.isPending}
                  disabled={Object.keys(draft).length === 0}
                >
                  Simpan perubahan
                </Button>
              </div>
            </form>
          </CardBody>
        </Card>
      </div>
    </>
  );
}
